import { Theme, Tooltip, withStyles } from '@material-ui/core';
import _ from 'lodash';
import moment from 'moment';
import { client } from '..';
import { FetchPolicy } from 'apollo-client';
import { DocumentNode } from 'graphql';
import { AIPrompt } from '../hooks/useAIPromptQuery';
import { UserFeatureEnum } from '../types/UserFeature';
import { mainTheme } from '../Theme';
import { LocalStorageEnum } from '../enums/LocalStorageEnum';

export interface ApplicationTerminology {
    guid: string;
    labelKey: string;
    singularLabel: string | null;
    pluralLabel: string | null;
    defaultPluralLabel: string;
    defaultSingularLabel: string;
    helperText: string | null;
    tooltip: string | null;
    internalDisplayLabel: string | null;
}

export function getApplicationTerminology(labelKey: string): ApplicationTerminology | undefined {
    const storedTerminology = localStorage.getItem(LocalStorageEnum.ApplicationTerminology);

    if (!storedTerminology) {
        return undefined;
    }

    try {
        const terminology = JSON.parse(storedTerminology) as ApplicationTerminology[];
        return Array.isArray(terminology)
            ? terminology.find((item) => item.labelKey === labelKey)
            : undefined;
    } catch {
        return undefined;
    }
}

export function updateApplicationTerminologyLocalStorage(
    labelKey: string,
    updates: Pick<ApplicationTerminology, 'singularLabel' | 'pluralLabel' | 'helperText' | 'tooltip'>,
): void {
    const storedTerminology = localStorage.getItem(LocalStorageEnum.ApplicationTerminology);

    if (!storedTerminology) {
        return;
    }

    try {
        const terminology = JSON.parse(storedTerminology) as ApplicationTerminology[];

        if (!Array.isArray(terminology)) {
            return;
        }

        const updatedTerminology = terminology.map((item) => (
            item.labelKey === labelKey ? { ...item, ...updates } : item
        ));

        localStorage.setItem(LocalStorageEnum.ApplicationTerminology, JSON.stringify(updatedTerminology));
    } catch {
        return;
    }
}

export function arrayToString(arr: (string | number | null)[]) {
    return arr.filter(Boolean).join(', ');
}

export function arrayToStringWithSeparator(arr: (string | number | null)[], separtor: string) {
    return arr.filter(Boolean).join(separtor);
}

export function addEllipsis(text: string, maxLength: number): string {
    return text.length > maxLength ? text.slice(0, maxLength - 3) + '...' : text;
}

export function removeTagsAndStyles(htmlString: string): string {
    // Remove HTML tags and &nbsp; using regex
    const withoutTagsAndNbsp = htmlString.replace(/(<([^>]+)>|&nbsp;)/gi, '');
  
    // Remove inline styles using regex
    const withoutStyles = withoutTagsAndNbsp.replace(/style\s*=\s*".*?"/gi, '');
  
    // Remove remaining whitespace and line breaks
    const withoutWhitespace = withoutStyles.replace(/\s+/g, ' ').trim();
  
    return withoutWhitespace;
  }

export const LightTooltip = withStyles((theme: Theme) => ({
    arrow: {
        color: theme.palette.common.white,
        '&::before': {
            // backgroundColor: 'blue',
            boxShadow: theme.shadows[1],
        }
    },
    tooltip: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: '14px',
      maxWidth: '800px',
      fontFamily: 'Nunito, Arial, Helvetica, sans-serif',
      padding: '10px',
    },
}))(Tooltip);

export const finalFormValidateEmail = (value?: string) => {
        
    const emailRegex = /^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    let error: string = '';

    if (!value) {
        return error;
    }
    
    if (!emailRegex.test(value)) {
        error = 'Email is not valid';
    }
    return error;
};

export function separateName(name: string): { firstName: string, lastName: string } {
    const words = name.split(' ');
    const firstName = words.shift(); // Removes and returns the first element of the array
    const lastName = words.join(' ');
  
    return {
      firstName: firstName || '', // If firstName is undefined, set it to an empty string
      lastName: lastName || '', // If lastName is undefined, set it to an empty string
    };
  }
  
export const addBusinessDays = (originalDate: Date, numDaysToAdd: number) => {
    const Sunday = 0;
    const Saturday = 6;
    let daysRemaining = numDaysToAdd;

    const newDate = moment(new Date(originalDate.valueOf()));

    while (daysRemaining > 0) {
        newDate.add(1, 'days');
        if (newDate.day() !== Sunday && newDate.day() !== Saturday) {
            daysRemaining--;
        }
    }

    return newDate;
};

// convert muitext editor content that compose using :blocks then convert to react-wysyg
// tslint:disable-next-line: no-any
export function convertToDraftWysiwygContent(muiContent: any) {
    // tslint:disable-next-line: no-any
    const contentBlocks = muiContent.blocks.map((muiBlock: any) => ({
      text: muiBlock.text,
      type: muiBlock.type || 'unstyled', // Use 'unstyled' if type is missing
      depth: muiBlock.depth || 0,
      inlineStyleRanges: muiBlock.inlineStyleRanges || [],
      entityRanges: muiBlock.entityRanges || [],
      data: muiBlock.data || {},
    }));
  
    return {
      entityMap: muiContent.entityMap || {},
      blocks: contentBlocks,
    };
}

export function checkIfIntialValuesIsEqualToValues<T>(initialValues: T, values: T) {
    const keys = _.union(_.keys(initialValues), _.keys(values));
  
    return _.every(keys, (key) => {
      const a = initialValues[key];
      const b = values[key];
  
      if (_.isUndefined(a) || _.isNull(a)) {
        return _.isUndefined(b) || _.isNull(b);
      }
  
      return _.isEqual(a, b);
    });
}

// tslint:disable-next-line: no-any
export function retrieveQueryData<queryParams extends object, queryResult extends object, queryError = any>(
    query: DocumentNode,
    queryParams: queryParams,
    refreshData: boolean,
    onSuccess: (data: queryResult) => void,
    onError: (reason: queryError) => void,
) {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query<queryResult, queryParams>({
            query: query,
            variables: { ...queryParams },
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason) => {
            onError(reason);
        });
}

export function capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export function hashString(input: string): number {
    var hash = 0, i, chr;
    if (input.length === 0) {
        return hash;
    }
    for (i = 0; i < input.length; i++) {
        chr = input.charCodeAt(i);
        // tslint:disable-next-line:no-bitwise
        hash = ((hash << 5) - hash) + chr - 1;
        // tslint:disable-next-line:no-bitwise
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export function isDateDue(value: Date) {
     // Compare only the date part, ignore the time
    const today = moment().startOf('day');
    const compareDate = moment(value).startOf('day');
    return compareDate.isBefore(today);
}

export function getPrompt(data: AIPrompt[], feature: keyof typeof UserFeatureEnum) {
    // Get feature name from enum key
    const featureStr = feature.startsWith('has') ? feature.slice(3) : feature;

    const featureResult = data.find(
        (item) =>
            item.childFeatureName?.trim().toLowerCase() === featureStr.trim().toLowerCase() ||
            item.featureName?.trim().toLowerCase() === featureStr.trim().toLowerCase()
    );

    return featureResult?.prompt;
}

export function normalizeFileName(name: string) {
    try {
        // Only decode if it looks encoded
        return /%[0-9A-Fa-f]{2}/.test(name) ? decodeURIComponent(name) : name;
    } catch {
        return name;
    }
}

export function getDurationType(startDate: moment.Moment, endDate: moment.Moment): string {
    if (!startDate || !endDate || !startDate.isValid() || !endDate.isValid()) return '';

    let tempStart = startDate.clone();
    let months = endDate.diff(tempStart, 'months') + 1; // Add 1 to make it inclusive (e.g., Dec to March = 4 months, not 3)
    tempStart.add(months - 1, 'months'); // Adjust tempStart for correct days calculation
    let days = endDate.diff(tempStart, 'days');

    let result = [];
    if (months > 0) result.push(`${months} Month${months > 1 ? 's' : ''}`);
    if (days > 0) result.push(`${days} Day${days > 1 ? 's' : ''}`);
    if (result.length === 0) return '0 Day';
    return result.join(' and ');
}

export const convertMentionInCommentToHtml = (comment: string, label?: string) => {
    let regex = /@\[.+?\]\(.+?\)/gm;
    let displayRegex = /@\[.+?\]/g;
    let idRegex = /\(.+?\)/g;
    let matches = comment.match(regex);
    // tslint:disable-next-line: no-any
    let arr: any = [];
    
    // tslint:disable-next-line: no-unused-expression
    matches &&
        // tslint:disable-next-line: no-any
        matches.forEach((m: any) => {
            let id = m.match(idRegex)[0].replace('(', '').replace(')', '');
            let display = m.match(displayRegex)[0].replace('@[', '').replace(']', '');

            arr.push({ id: id, display: display });
        });
    let newComment = comment.split(regex);
    let output = label ? label + ': ' : '';
    for (let i = 0; i < newComment.length; i++) {
        const c = newComment[i];
        if (i === newComment.length - 1) {
            output += c;
        } else {
            output += c + `<span mentioned_id="${arr[i].id}">${arr[i].display.split('|')[0]}</span>`;
        }
    }

    return output;
}; 
    
export const retrieveQueryDataAsync = <P extends object, R extends object>(option: {
    query: DocumentNode,
    params: P,
    refreshData: boolean,
}): Promise<R> => {
    return new Promise((resolve, reject) => {
        retrieveQueryData<P, R>(
            option.query,
            option.params,
            option.refreshData,
            (data) => resolve(data),
            (reason) => reject(reason)
        );
    });
};

export function capitalizeToPascalCase(str: string): string {
    return str.toLowerCase()        .split(/[_\s]+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
};

export const TooltipUI = withStyles((theme: Theme) => ({
    tooltip: {
        backgroundColor: mainTheme.BrandColors.TemplatePrimary,
        color: '#ffffff',
        maxWidth: 220,
        fontSize: 14,
        border: '1px solid #dadde9',
    },
}))(Tooltip);